import { memo } from "react";
import type { TabMemory } from "@/routing/core/locations";
import { KeepAliveContainer } from "@/routing/keep-alive/KeepAliveContainer";
import { ProjectLayout } from "./project-layout";

interface ProjectContainerProps {
  tabId: string;
  projectId: string;
  isActive: boolean;
  currentPath: string;
  searchParams: Record<string, unknown>;
  memory: TabMemory;
}

/**
 * ProjectContainer wraps a single project's layout with visibility control.
 *
 * Uses CSS-based visibility to hide/show projects without unmounting them.
 * This preserves all React state, DOM state, and effect subscriptions
 * (including Convex real-time subscriptions) for instant tab switching.
 *
 * Key implementation details:
 * - Uses Tailwind classes for responsive layout (flex/hidden)
 * - Uses position: absolute + visibility: hidden for hidden projects to ensure
 *   they don't affect layout or receive focus/keyboard events
 * - Memoized to prevent unnecessary re-renders when other projects change
 * - Sets aria-hidden for accessibility
 *
 * Note: The left sidebar for each project is rendered separately by
 * ProjectSidebarsHost at the root level to maintain full-height layout.
 *
 * Why not React 19.2's <Activity>? While Activity preserves React state,
 * it unmounts effects when hidden, causing Convex subscriptions to disconnect
 * and reconnect on every tab switch. This creates visible flickering and
 * loading states. CSS visibility provides instant switching with no flicker.
 */
export const ProjectContainer = memo(function ProjectContainer({
  tabId,
  projectId,
  isActive,
  currentPath,
  searchParams,
  memory,
}: ProjectContainerProps) {
  return (
    <KeepAliveContainer
      active={isActive}
      variant="block"
      className="flex flex-1 overflow-hidden w-full h-full"
      data-project-id={projectId}
    >
      <ProjectLayout
        tabId={tabId}
        projectId={projectId}
        currentPath={currentPath}
        searchParams={searchParams}
        memory={memory}
      />
    </KeepAliveContainer>
  );
});
